import React, { useState, useRef, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";

const BEFORE_SRC =
  "https://customer-assets.emergentagent.com/job_clearview-services/artifacts/y6nmfb3q_IMG_4175.jpeg";
const AFTER_SRC =
  "https://customer-assets.emergentagent.com/job_clearview-services/artifacts/he7zbgrn_IMG_4176.jpeg";

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50); // percent of after-image visible from the left
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    setFromClientX(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    draggingRef.current = false;
  };

  // Keyboard a11y
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
  };

  // Subtle auto-nudge on mount to hint interactivity
  useEffect(() => {
    let dir = -1;
    const t1 = setTimeout(() => setPos(58), 600);
    const t2 = setTimeout(() => setPos(42), 1100);
    const t3 = setTimeout(() => setPos(50), 1600);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      void dir;
    };
  }, []);

  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="py-20 md:py-32 bg-white scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">
            See the difference
          </p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Drag to reveal the <span className="text-blue-600">transformation.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 font-medium">
            One soft-wash visit. Years of mildew, dirt, and algae lifted off
            the siding — without harsh pressure.
          </p>
        </div>

        <div
          ref={containerRef}
          data-testid="before-after-slider"
          className="relative w-full aspect-[16/10] md:aspect-[16/9] rounded-3xl overflow-hidden ring-1 ring-slate-900/10 shadow-2xl select-none touch-none cursor-ew-resize bg-slate-900"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {/* Before image (full) */}
          <img
            src={BEFORE_SRC}
            alt="Dirty siding before pressure washing"
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          />
          {/* After image (clipped) */}
          <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          >
            <img
              src={AFTER_SRC}
              alt="Clean siding after pressure washing"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>

          {/* Labels */}
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-black tracking-widest uppercase shadow-md">
            After
          </span>
          <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-slate-700 text-[10px] font-black tracking-widest uppercase shadow-md">
            Before
          </span>

          {/* Divider line */}
          <div
            className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_18px_rgba(0,0,0,0.4)] pointer-events-none"
            style={{ left: `calc(${pos}% - 1.5px)` }}
          />

          {/* Handle */}
          <button
            type="button"
            data-testid="before-after-handle"
            aria-label="Drag to compare before and after"
            aria-valuenow={Math.round(pos)}
            aria-valuemin={0}
            aria-valuemax={100}
            role="slider"
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 md:h-14 md:w-14 rounded-full bg-white grid place-items-center shadow-xl ring-4 ring-blue-600 hover:scale-110 active:scale-95 transition-transform focus:outline-none focus-visible:ring-blue-700"
            style={{ left: `${pos}%` }}
          >
            <MoveHorizontal className="h-5 w-5 md:h-6 md:w-6 text-blue-600" strokeWidth={2.5} />
          </button>
        </div>

        <p className="mt-5 text-center text-xs md:text-sm font-semibold text-slate-500 uppercase tracking-wider">
          Drag the handle · Real Ohio job · Soft-wash treatment
        </p>
      </div>
    </section>
  );
};
